import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MapPin, Clock, Info, UserPlus, AlertTriangle, Users, ExternalLink, Globe } from "lucide-react";
import { buildProviderUrl, PROVIDERS } from "@/lib/booking";
import ProviderButtons from "@/components/ProviderButtons";
import { formatTime, formatTimeRange } from "@/lib/time";
import Time12Input from "@/components/Time12Input";
import { Sparkles, RotateCcw } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0,10);

function AddTemp({ households, onAdd }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", household_id: households[0]?.id || "", earliest_time:"17:30", preferred_time:"18:30", latest_time:"19:30", persist:false });
  useEffect(()=>{
    if (households[0]) setForm(f => f.household_id ? f : {...f, household_id: households[0].id});
  }, [households]);
  const submit = async () => {
    if (!form.first_name || !form.household_id) return toast.error("First name and household required");
    try {
      const { data } = await api.post("/members", { ...form, active: true, temporary: !form.persist });
      onAdd(data);
      toast.success(form.persist ? "Added to group" : "Added for this dinner");
      setOpen(false);
    } catch (e) { toast.error(formatError(e)); }
  };
  if (!open) return <Button variant="outline" size="sm" onClick={()=>setOpen(true)} data-testid="add-temp-guest-btn"><UserPlus className="w-4 h-4 mr-1"/>+ Add Person</Button>;
  return (
    <Card className="p-4 mt-2 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div><Label>First name</Label><Input data-testid="temp-first-name-input" value={form.first_name} onChange={e=>setForm({...form,first_name:e.target.value})}/></div>
        <div><Label>Last name</Label><Input value={form.last_name} onChange={e=>setForm({...form,last_name:e.target.value})}/></div>
        <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
        <div className="col-span-2">
          <Label>Household</Label>
          <select className="w-full border rounded-md h-10 px-3 bg-background" value={form.household_id} onChange={e=>setForm({...form,household_id:e.target.value})} data-testid="temp-household-select">
            {households.map(h=><option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div><Label>Earliest</Label><Time12Input value={form.earliest_time} onChange={v=>setForm({...form,earliest_time:v})} testId="temp-earliest-time"/></div>
        <div><Label>Preferred</Label><Time12Input value={form.preferred_time} onChange={v=>setForm({...form,preferred_time:v})} testId="temp-preferred-time"/></div>
        <div><Label>Latest</Label><Time12Input value={form.latest_time} onChange={v=>setForm({...form,latest_time:v})} testId="temp-latest-time"/></div>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="persist" checked={form.persist} onCheckedChange={v=>setForm({...form,persist:!!v})} data-testid="temp-persist-checkbox"/>
        <Label htmlFor="persist" className="text-sm">Add to permanent group</Label>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} data-testid="temp-add-submit-btn">Add</Button>
        <Button size="sm" variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
      </div>
    </Card>
  );
}

export default function PlanDinner() {
  const nav = useNavigate();
  const [date, setDate] = useState(todayISO());
  const [members, setMembers] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [bestEffortPrompt, setBestEffortPrompt] = useState(null);
  const [quorumFailed, setQuorumFailed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cuisineHints, setCuisineHints] = useState(null);

  const load = () => Promise.all([
    api.get("/members").then(r=>r.data),
    api.get("/households").then(r=>r.data),
    api.get("/cuisine-suggestions").then(r=>r.data).catch(()=>null),
  ]).then(([m,h,c])=>{
    setMembers(m); setHouseholds(h);
    setSelected(new Set(m.filter(x=>x.active && !x.temporary).map(x=>x.id)));
    setCuisineHints(c);
  });
  useEffect(() => { load(); }, []);

  const householdName = (id) => households.find(h=>h.id===id)?.name || "";
  const toggle = (id) => setSelected(s=>{ const n=new Set(s); n.has(id)?n.delete(id):n.add(id); return n; });

  const plan = async (allow_partial = false) => {
    setLoading(true);
    setResult(null); setBestEffortPrompt(null); setQuorumFailed(null);
    try {
      const { data } = await api.post("/dinners/plan", { dinner_date: date, participant_member_ids: [...selected], notes, allow_partial });
      if (data.quorum_failed) {
        setQuorumFailed(data);
        toast.error(`Not enough people — need ≥ ${data.min_quorum_members} members from ≥ ${data.min_quorum_households} households`);
      } else if (data.needs_confirmation) {
        setBestEffortPrompt(data);
        toast(`${data.available_count} of ${data.total} available — confirm to plan best-effort`);
      } else {
        setResult(data);
        toast.success(data.best_effort ? `Best-effort recommendation ready · ${data.participant_member_ids.length} attending` : "Recommendation ready");
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  const approve = async (restaurant_id, time, booking = null) => {
    try {
      await api.post(`/dinners/${result.id}/approve`, { restaurant_id, time });
      await api.post(`/dinners/${result.id}/reservation`, {
        status: "external_confirmation_required",
        provider: booking?.provider || "manual",
        provider_link: booking?.url || null,
        confirmation_number: null,
      });
      if (booking?.url) {
        await api.post(`/dinners/${result.id}/booking-intent`, {
          provider: booking.provider, provider_url: booking.url,
        }).catch(() => {});
        window.open(booking.url, "_blank", "noopener,noreferrer");
        toast.success(`Approved. ${booking.label} opened — we'll remind you in 10 min if you don't record the confirmation.`);
      } else {
        toast.success("Approved. Complete the reservation externally, then record the confirmation.");
      }
      nav(`/dinners/${result.id}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const [refreshing, setRefreshing] = useState(false);
  const refreshOptions = async () => {
    if (!result?.id) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/dinners/${result.id}/refresh-recommendations`);
      setResult(data);
      toast.success("Fresh options loaded");
      // Scroll back to the main recommendation card so the user sees the new pick
      setTimeout(() => {
        document.querySelector('[data-testid="recommendation-card-main"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(formatError(e));
    } finally { setRefreshing(false); }
  };

  return (
    <div data-testid="plan-stepper-container" className="space-y-8">
      <div>
        <div className="overline mb-1">Plan a dinner</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Tonight's plan</h1>
      </div>

      <Card className="p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Dinner date</Label>
            <Input data-testid="step-date-picker" type="date" value={date} onChange={e=>setDate(e.target.value)} />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g. celebrating Bob's birthday"/>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Participants</div>
            <AddTemp households={households} onAdd={(m)=>{ setMembers(list=>[...list,m]); setSelected(s=>new Set([...s,m.id])); }}/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {members.filter(m=>m.active).map(m => (
              <label key={m.id} data-testid="step-availability-toggle-household" className="flex items-center gap-3 p-3 rounded-lg border hover:bg-secondary/60 cursor-pointer">
                <Checkbox checked={selected.has(m.id)} onCheckedChange={()=>toggle(m.id)} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{m.first_name} {m.last_name} {m.temporary && <span className="text-xs text-amber-700">(temp)</span>}</div>
                  <div className="text-xs text-muted-foreground">{householdName(m.household_id)} · {formatTime(m.preferred_time)}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button size="lg" onClick={()=>plan(false)} disabled={loading || selected.size===0} data-testid="run-plan-btn">
            {loading? "Analyzing…" : "Analyze & recommend"}
          </Button>
        </div>
      </Card>

      {cuisineHints?.suggestions?.length > 0 && (
        <Card className="p-5 border-primary/40 bg-primary/5" data-testid="cuisine-suggestions-card">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-primary mt-1 shrink-0"/>
            <div className="flex-1">
              <div className="overline mb-1">Fresh cuisine ideas</div>
              <div className="font-serif text-lg mb-2">Try something the group hasn't had in a while</div>
              <div className="flex flex-wrap gap-2" data-testid="cuisine-suggestions-list">
                {cuisineHints.suggestions.map(s => (
                  <div key={s.cuisine} data-testid="cuisine-suggestion-chip"
                        className="text-xs px-3 py-1.5 rounded-full border bg-background flex items-center gap-2">
                    <span className="font-medium">{s.cuisine}</span>
                    <span className="text-muted-foreground">
                      {s.count === 0
                        ? "never eaten"
                        : s.days_since != null
                          ? `last: ${s.days_since}d ago`
                          : ""}
                    </span>
                  </div>
                ))}
              </div>
              {cuisineHints.recent?.length > 0 && (
                <div className="text-xs text-muted-foreground mt-3">
                  Recently eaten: {cuisineHints.recent.slice(0,3).map(r=>r.cuisine).join(" · ")}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {bestEffortPrompt && (
        <Card data-testid="best-effort-prompt" className="p-6 border-amber-500 border-2 bg-amber-50/70 dark:bg-amber-900/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 shrink-0"/>
            <div className="flex-1">
              <div className="overline mb-1">Partial availability</div>
              <h3 className="font-serif text-2xl">
                {bestEffortPrompt.available_count} of {bestEffortPrompt.total} available
              </h3>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <Users className="w-4 h-4"/> {bestEffortPrompt.available_household_count} household{bestEffortPrompt.available_household_count===1?"":"s"} on board · quorum met
              </div>
              <div className="mt-3 text-sm">
                <div className="overline mb-1">Can't make it</div>
                <ul className="space-y-1">
                  {bestEffortPrompt.unavailable.map(u => (
                    <li key={u.member_id} className="text-xs flex items-start gap-1 text-muted-foreground">
                      <XCircle className="w-3 h-3 mt-0.5 shrink-0"/>{u.first_name} {u.last_name} — {u.reason}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <Button onClick={()=>plan(true)} disabled={loading} data-testid="book-anyway-btn">
                  {loading? "Analyzing…" : `Book with the ${bestEffortPrompt.available_count} anyway`}
                </Button>
                <Button variant="outline" onClick={()=>setBestEffortPrompt(null)} data-testid="cancel-best-effort-btn">Pick another date</Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {quorumFailed && (
        <Card data-testid="quorum-failed-notice" className="p-6 border-destructive border-2 bg-destructive/5">
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-destructive mt-1 shrink-0"/>
            <div>
              <div className="overline mb-1">Not enough people</div>
              <h3 className="font-serif text-2xl">Below quorum</h3>
              <p className="text-sm mt-2">
                Only <strong>{quorumFailed.available_count}</strong> member{quorumFailed.available_count===1?"":"s"} from{" "}
                <strong>{quorumFailed.available_household_count}</strong> household{quorumFailed.available_household_count===1?"":"s"} can attend.
                Group needs at least <strong>{quorumFailed.min_quorum_members}</strong> members and <strong>{quorumFailed.min_quorum_households}</strong> households.
              </p>
              <p className="text-xs text-muted-foreground mt-2">Try a different date, or lower the quorum in Admin → Config.</p>
            </div>
          </div>
        </Card>
      )}

      {result && <Recommendation result={result} onApprove={approve} members={members} households={households} onRefresh={refreshOptions} refreshing={refreshing} />}
    </div>
  );
}

function TravelBar({ minutes, miles, prefMax=30 }) {
  const pct = Math.min(100, (minutes / (prefMax * 1.5)) * 100);
  const bad = minutes > prefMax;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${bad ? "bg-destructive" : "bg-primary"}`} style={{width:`${pct}%`}}/>
      </div>
      <span className="mono text-sm w-24 text-right">
        {minutes} min{miles != null ? <span className="text-muted-foreground"> · {miles.toFixed(1)} mi</span> : null}
      </span>
    </div>
  );
}

// Sort provider buttons so the restaurant's remembered preferred provider comes first.
// (kept for legacy call sites — new callers should use <ProviderButtons/>)
function orderedProviders(preferredId) {
  if (!preferredId) return PROVIDERS;
  const preferred = PROVIDERS.find(p => p.id === preferredId);
  if (!preferred) return PROVIDERS;
  return [preferred, ...PROVIDERS.filter(p => p.id !== preferredId)];
}

function CandidateCard({ c, primary, onApprove, time, date, party, households = [] }) {
  const [open, setOpen] = useState(primary);
  const bookingCtx = {
    name: c.restaurant.name, city: c.restaurant.city,
    date, time, party,
  };
  const householdById = Object.fromEntries(households.map(h => [h.id, h]));
  const directionsUrl = (h) => {
    // Prefer the street address the user typed in household setup — Google re-resolves it
    // every click, which is more accurate than lat/lng that were geocoded once at setup.
    // Households store the line-1 in `street`; restaurants store it in `address`.
    const fullAddr = (o) => [o?.street || o?.address, o?.city, o?.state, o?.zip_code]
      .map(s => (s || "").trim()).filter(Boolean).join(", ");
    const destAddr = fullAddr(c.restaurant);
    const dest = destAddr
      ? encodeURIComponent(destAddr)
      : (c.restaurant.latitude != null && c.restaurant.longitude != null
          ? `${c.restaurant.latitude},${c.restaurant.longitude}`
          : encodeURIComponent(`${c.restaurant.name}, ${c.restaurant.city}, ${c.restaurant.state}`));
    const orgAddr = fullAddr(h || {});
    const origin = orgAddr
      ? encodeURIComponent(orgAddr)
      : (h && h.latitude != null && h.longitude != null
          ? `${h.latitude},${h.longitude}`
          : encodeURIComponent(h?.name || ""));
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`;
  };
  return (
    <Card data-testid={primary?"recommendation-card-main":"alternative-candidate-card"} className={`p-6 ${primary ? "border-primary border-2" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline">{primary ? "Recommended" : "Alternative"}</div>
          <h3 data-testid="recommendation-title" className="font-serif text-2xl mt-1">{c.restaurant.name}</h3>
          <div className="text-sm text-muted-foreground mt-1">{c.restaurant.cuisine} · {"$".repeat(c.restaurant.price_level)} · {c.restaurant.city}, {c.restaurant.state}</div>
        </div>
        <div className="text-right">
          <div data-testid="recommendation-match-score-badge" className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary mono text-sm">score {c.score}</div>
        </div>
      </div>
      <div className="mt-5 space-y-2" data-testid="household-driving-time-matrix">
        {c.travel.map(t => {
          const h = householdById[t.household_id];
          return (
            <a key={t.household_id}
                href={directionsUrl(h)}
                target="_blank"
                rel="noreferrer"
                data-testid="driving-time-household-item"
                title={`Open directions in Google Maps from ${t.household_name} to ${c.restaurant.name}`}
                className="block rounded-md hover:bg-secondary/50 focus:bg-secondary/50 p-1 -m-1 transition-colors group">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3"/>
                  <span className="group-hover:text-primary group-hover:underline underline-offset-2">{t.household_name}</span>
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity"/>
                </span>
              </div>
              <TravelBar minutes={t.minutes} miles={t.miles}/>
            </a>
          );
        })}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-secondary p-2"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Avg</div><div className="mono">{c.avg_min}m</div></div>
        <div className="rounded-lg bg-secondary p-2"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Longest</div><div className="mono">{c.max_min}m</div></div>
        <div className="rounded-lg bg-secondary p-2"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Spread</div><div className="mono">{c.spread}m</div></div>
      </div>
      <button data-testid="explainability-why-this-restaurant-toggle" onClick={()=>setOpen(o=>!o)} className="mt-4 text-sm text-primary flex items-center gap-1">
        <Info className="w-4 h-4"/> Why this restaurant?
      </button>
      {open && (
        <div data-testid="explainability-panel" className="mt-2 rounded-lg bg-secondary/50 p-4 space-y-1 text-sm">
          {c.reasons.map((r,i)=><div key={`reason-${i}-${r.slice(0,20)}`} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0"/><span>{r}</span></div>)}
        </div>
      )}
      <div className="mt-5 space-y-2">
        <Button data-testid={primary?"approve-recommendation-btn":"approve-alternative-btn"} onClick={()=>onApprove(c.restaurant.id, time)} className="w-full">
          Confirm &amp; Reserve
        </Button>
        <ProviderButtons
          ctx={bookingCtx}
          preferredId={c.restaurant.preferred_provider}
          testidSuffix={primary ? "-main" : ""}
          onOpen={(p, url)=>onApprove(c.restaurant.id, time, { provider: p.id, label: p.label, url })}
        />
        {c.restaurant.website && (
          <Button variant="ghost" size="sm" asChild data-testid="restaurant-website-link">
            <a href={c.restaurant.website} target="_blank" rel="noreferrer" className="flex items-center gap-1">
              <Globe className="w-3 h-3"/>Site
            </a>
          </Button>
        )}
        <p className="text-[10px] text-muted-foreground text-center">Booking opens in a new tab. Come back here to record your confirmation number.</p>
      </div>
    </Card>
  );
}

function Recommendation({ result, onApprove, members, households, onRefresh, refreshing }) {
  const rec = result.recommendation?.detail;
  const alts = (result.alternatives || []).map(a => a.detail);
  const time = result.recommended_time;
  const date = result.dinner_date;
  const party = result.participant_member_ids?.length || 0;
  const unavailable = result.unavailable || [];
  const memberName = (id) => members.find(m=>m.id===id)?.first_name || id;
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="overline mb-2">Common dinner window</div>
            <div className="font-serif text-2xl">{formatTimeRange(result.common_window.earliest, result.common_window.latest)}</div>
            <div className="text-sm mt-1 flex items-center gap-1 text-muted-foreground"><Clock className="w-4 h-4"/> Recommended time: <span className="mono">{formatTime(time)}</span></div>
          </div>
          <div>
            <div className="overline mb-2">Participants</div>
            <div className="text-sm">{result.participant_member_ids.map(memberName).join(", ")}</div>
            {unavailable.length > 0 && (
              <div className="mt-3">
                <div className="overline mb-1">Unavailable</div>
                {unavailable.map(u => (
                  <div key={u.member_id} className="text-xs text-muted-foreground flex gap-1 items-start"><XCircle className="w-3 h-3 mt-0.5 shrink-0"/>{memberName(u.member_id)} — {u.reason}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
      {rec && <CandidateCard c={rec} primary onApprove={onApprove} time={time} date={date} party={party} households={households}/>}
      <div className="grid md:grid-cols-2 gap-6">
        {alts.map((c) => <CandidateCard key={c.restaurant.id} c={c} primary={false} onApprove={onApprove} time={time} date={date} party={party} households={households}/>)}
      </div>
      {onRefresh && (
        <div className="flex flex-col items-center gap-2 pt-2" data-testid="refresh-options-wrap">
          <p className="text-xs text-muted-foreground">Not feeling any of these?</p>
          <Button
            variant="outline"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="refresh-recommendations-btn"
            className="gap-2"
          >
            <RotateCcw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}/>
            {refreshing ? "Finding more…" : "Show different options"}
          </Button>
        </div>
      )}
    </div>
  );
}
